import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getCookie } from '../utils/utils';
import { startListingPriceEstimation } from '../utils/startListingPriceEstimation';
import ReadyToPublishStep from '../AppContent/NewLandingPage/AddMyPlaceFlow/steps/ReadyToPublishStep';
import BuildingConfirmStep from '../AppContent/NewLandingPage/AddMyPlaceFlow/steps/BuildingConfirmStep';
import SizeStep from '../AppContent/NewLandingPage/AddMyPlaceFlow/steps/SizeStep';
import RoomsStep from '../AppContent/NewLandingPage/AddMyPlaceFlow/steps/RoomsStep';
import FlowMap from '../AppContent/NewLandingPage/AddMyPlaceFlow/FlowMap';
import WizardHeader from '../AppContent/NewLandingPage/AddMyPlaceFlow/WizardHeader';
import houseIcon from '../common/icons/house.png';
import {
  buildingToListingState,
  ClickedBuildingState,
} from '../AppContent/NewLandingPage/AddMyPlaceFlow/buildingToListingState';
import { ImageLightbox } from '../ImageLightbox';
import {
  useListingImageUpload,
  ListingImageData,
} from '../../hooks/useListingImageUpload';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { BuildingWithConvertedAddresses, LatLng } from '../utils/types';
import {
  AddMyPlaceFormState,
  BuildingTypeValue,
  RenovationStatus,
  MAX_PROPERTY_DESCRIPTION_LENGTH,
} from '../AppContent/NewLandingPage/AddMyPlaceFlow/types';
// Reuse the wizard's overlay/card/header/map chrome (amp-flow*) for visual parity.
import '../AppContent/NewLandingPage/AddMyPlaceFlow/AddMyPlaceFlow.scss';
import './EditListingModal.scss';

interface EditListingModalProps {
  isOpen: boolean;
  // The off-market-willingness id to edit; the modal fetches its own details.
  willingnessId: string;
  onClose: () => void;
  // Called after a successful save so the caller can refresh its listing view.
  onSaved: () => void;
}

// Kept in sync with the amp-flow overlay/card CSS slide transition duration.
const ANIMATION_MS = 600;
// Framing zoom for the building picker map (>= FlowMap's building-dots minimum).
const PICKER_ZOOM = 17;

/**
 * Build the wizard form state from a fetched listing's stored property_* fields.
 * The address object is flattened into the three string fields the wizard's
 * display derivations expect (street+number, city, postcode). A stored price
 * of 0 opens as "no set price", the way every other surface reads it
 * (sellerPriceDisplay, has_seller_price), so it never trips the JAA-458 minimum.
 */
function listingToForm(data: {
  address?: {
    street?: string;
    houseNumber?: string;
    city?: string;
    postcode?: string;
  };
  property_description?: string;
  property_size?: number | string;
  property_rooms?: number | string;
  property_floor?: number | string;
  property_has_elevator?: boolean;
  property_has_balcony?: boolean;
  property_renovation_status?: string;
  property_asking_price?: number | string;
  property_building_type?: string;
}): AddMyPlaceFormState {
  const address = data.address ?? {};
  return {
    address: [address.street, address.houseNumber]
      .filter(Boolean)
      .join(' ')
      .trim(),
    area: address.city ?? '',
    postal: address.postcode ?? '',
    buildingType:
      (data.property_building_type as BuildingTypeValue) ||
      'apartment_building',
    size: data.property_size != null ? String(data.property_size) : '',
    rooms: data.property_rooms != null ? String(data.property_rooms) : '',
    description: data.property_description ?? '',
    floor: data.property_floor != null ? String(data.property_floor) : '',
    renovationStatus: (data.property_renovation_status ??
      '') as RenovationStatus,
    hasElevator: data.property_has_elevator ?? null,
    hasBalcony: data.property_has_balcony ?? null,
    askingPrice:
      data.property_asking_price != null &&
      Number(data.property_asking_price) !== 0
        ? String(data.property_asking_price)
        : '',
  };
}

// The listing point from the stored GeoJSON Point (coordinates are [lng, lat]),
// falling back to the address subdoc coordinates. Seeds the picker map.
function listingLocationFromData(data: {
  geometry?: { coordinates?: number[] };
  address?: { coordinates?: { lat?: number; lng?: number } };
}): LatLng | null {
  const coords = data.geometry?.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    return { lat: coords[1], lng: coords[0] };
  }
  const ac = data.address?.coordinates;
  if (ac && typeof ac.lat === 'number' && typeof ac.lng === 'number') {
    return { lat: ac.lat, lng: ac.lng };
  }
  return null;
}

/**
 * Edit an existing sell listing using the new "Add my place" create UI: the
 * detail+photo step (ReadyToPublishStep), plus a "pick from map" building picker
 * (FlowMap + BuildingConfirmStep) that can MOVE the listing to a different
 * building. Prefilled from a GET, saved with a PUT; a building move sends
 * `selected_building`. Self-contained — calls `onSaved` after a successful save.
 * Renders above the ModalController's top band (z 2520) so it never opens behind
 * the profile "My listings" modal.
 */
const EditListingModal: React.FC<EditListingModalProps> = ({
  isOpen,
  willingnessId,
  onClose,
  onSaved,
}) => {
  const { t, i18n } = useTranslation();
  const {
    images,
    uploadError,
    isRegenerating,
    enhancementStatus,
    fetchEnhancementStatus,
    uploadFiles,
    removeImage,
    reorderImages,
    toggleImageVersion,
    regenerateImage,
    resetImages,
    restoreImages,
  } = useListingImageUpload();

  const [isMounted, setIsMounted] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [view, setView] = useState<
    'details' | 'pickBuilding' | 'size' | 'rooms'
  >('details');
  const [form, setForm] = useState<AddMyPlaceFormState | null>(null);
  const [loadError, setLoadError] = useState(false);
  // The listing's AI estimate, display-only: a completed one is shown by
  // ReadyToPublishStep when no asking price is set (JAA-329 #15), and a pending
  // one swaps the price tip to the "Jaakko is estimating" copy (JAA-369). Kept
  // out of the form state so the PUT body never echoes it back.
  const [estimate, setEstimate] = useState<{
    price: number | null;
    currency: string | null;
    status: 'pending' | 'completed' | 'failed' | null;
  }>({ price: null, currency: null, status: null });
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  // Building state, seeded from the GET; `clickedBuilding` is set only when the
  // user picks a different building on the map, which is how we detect a move.
  const [listingLocation, setListingLocation] = useState<LatLng | null>(null);
  const [clickedBuilding, setClickedBuilding] =
    useState<ClickedBuildingState>(null);
  const [originalBuildingId, setOriginalBuildingId] = useState('');

  useBodyScrollLock(isMounted);

  // Keep the modal mounted through the close animation, then unmount.
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return undefined;
    }
    setIsActive(false);
    const timer = setTimeout(() => setIsMounted(false), ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Trigger the slide-in transition on the frame after mount.
  useEffect(() => {
    if (!isMounted) return undefined;
    const raf = requestAnimationFrame(() => setIsActive(true));
    return () => cancelAnimationFrame(raf);
  }, [isMounted]);

  // Fetch the listing's current details + photos + location each time the modal
  // opens so the form always reflects the latest saved state.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setLoadError(false);
    setForm(null);
    setView('details');
    setClickedBuilding(null);
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/off-market-willingness/${willingnessId}`,
          { credentials: 'include' },
        );
        if (!res.ok) throw new Error('Failed to load listing');
        const data = await res.json();
        if (cancelled) return;
        setForm(listingToForm(data));
        setEstimate({
          price:
            typeof data.estimated_price === 'number'
              ? data.estimated_price
              : null,
          currency: data.estimated_price_currency ?? null,
          status: data.estimate_status ?? null,
        });
        setOriginalBuildingId(data.building_id || '');
        setListingLocation(listingLocationFromData(data));
        restoreImages(
          (data.images ?? []).map((img: ListingImageData) => ({
            id: img.id || img.url,
            url: img.url,
            thumbnailUrl: img.thumbnailUrl || img.url,
            originalUrl: img.originalUrl,
            originalThumbnailUrl: img.originalThumbnailUrl,
            enhancedUrl: img.enhancedUrl,
            enhancedThumbnailUrl: img.enhancedThumbnailUrl,
            isDisplayOptimized: img.isDisplayOptimized,
          })) as ListingImageData[],
        );
        fetchEnhancementStatus();
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, willingnessId, restoreImages, fetchEnhancementStatus]);

  // Drop staged images once the modal has fully closed.
  useEffect(() => {
    if (!isMounted) resetImages();
  }, [isMounted, resetImages]);

  const updateForm = useCallback((updates: Partial<AddMyPlaceFormState>) => {
    setForm((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  // Map click: adopt the picked building's address + location (shared with the
  // create flow via buildingToListingState). Only a coordinate-bearing building
  // updates clickedBuilding — that's what marks a real move on save.
  const handleSelectBuilding = useCallback(
    (building: BuildingWithConvertedAddresses) => {
      const selection = buildingToListingState(building);
      updateForm(selection.formPatch);
      if (selection.listingLocation)
        setListingLocation(selection.listingLocation);
      if (selection.clickedBuilding)
        setClickedBuilding(selection.clickedBuilding);
    },
    [updateForm],
  );

  // Escape / close: dismiss the photo lightbox first, then the map picker, else
  // close the modal.
  const handleClose = useCallback(() => {
    if (lightboxIndex !== null) {
      setLightboxIndex(null);
      return;
    }
    if (view !== 'details') {
      setView('details');
      return;
    }
    onClose();
  }, [lightboxIndex, view, onClose]);

  useEffect(() => {
    if (!isMounted) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMounted, handleClose]);

  /**
   * Saves the edit, then starts the replacement estimate when the server minted
   * a capability. A daily-cap refusal is told to the user, and any start that
   * did not happen undoes My Listings' optimistic estimating row.
   */
  const handlePublish = useCallback(async () => {
    if (!form) return;
    const validImages = images.filter(
      (image) =>
        image.id &&
        image.url &&
        image.thumbnailUrl &&
        !image.error &&
        !image.isUploading,
    );
    const movedBuilding =
      !!clickedBuilding && clickedBuilding.buildingId !== originalBuildingId;
    try {
      setIsPublishing(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/off-market-willingness/${willingnessId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
          },
          body: JSON.stringify({
            property_description:
              form.description
                .trim()
                .slice(0, MAX_PROPERTY_DESCRIPTION_LENGTH) || null,
            property_size: form.size.trim() !== '' ? Number(form.size) : null,
            property_rooms: form.rooms.trim() === '' ? null : form.rooms,
            property_floor:
              form.floor.trim() !== '' ? Number(form.floor) : null,
            property_has_elevator: form.hasElevator,
            property_has_balcony: form.hasBalcony,
            property_renovation_status:
              form.renovationStatus === '' ? null : form.renovationStatus,
            property_asking_price:
              form.askingPrice.trim() !== '' ? Number(form.askingPrice) : null,
            property_building_type: form.buildingType,
            // Building move: reuse the exact selected_building shape the create
            // POST builds, so the server re-derives geometry/address consistently.
            ...(movedBuilding && clickedBuilding
              ? {
                  selected_building: {
                    id: clickedBuilding.buildingId,
                    centroid: listingLocation,
                    geometry: clickedBuilding.geometry,
                    addresses: [clickedBuilding.addressObject],
                    properties: {
                      name: `${form.address}, ${form.area}`,
                      addresses: [clickedBuilding.addressObject],
                      type: 'apartments',
                    },
                  },
                }
              : {}),
            property_images: validImages.map((image) => ({
              id: image.id,
              url: image.url,
              thumbnailUrl: image.thumbnailUrl,
              originalUrl: image.originalUrl ?? null,
              originalThumbnailUrl: image.originalThumbnailUrl ?? null,
              enhancedUrl: image.enhancedUrl ?? null,
              enhancedThumbnailUrl: image.enhancedThumbnailUrl ?? null,
              isDisplayOptimized: image.isDisplayOptimized === true,
            })),
          }),
        },
      );
      if (!response.ok) {
        try {
          const err = await response.json();
          if (err && err.error_code === 'SIZE_MUST_BE_GREATER_THAN_ZERO') {
            alert(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
            return;
          }
          if (err && err.error_code === 'LISTING_ALREADY_EXISTS_ON_BUILDING') {
            // Keep the modal open on the picker so the user can choose another.
            alert(
              t(
                'building.offMarket.modal.listingAlreadyOnBuilding',
                'You already have a listing on this building.',
              ),
            );
            setView('pickBuilding');
            return;
          }
        } catch (_) {
          // fall through to generic error
        }
        throw new Error('Failed to update listing');
      }
      // Same handoff as publishing: when the edit changed what the place is (or
      // moved it), the backend mints a one-time capability and the replacement
      // Jaakko estimate starts here. Edits to photos or the asking price mint
      // nothing, so nothing is re-run.
      const updated = await response.json().catch(() => ({}));
      if (updated.price_estimation_capability) {
        // Announce the re-estimate before starting it: the listing is not
        // stamped `pending` until the estimation service accepts the job, and
        // the list behind this modal should not show a stale price until then.
        window.dispatchEvent(
          new CustomEvent('jaakko:listing-estimating', {
            detail: { willingnessId },
          }),
        );
        void startListingPriceEstimation({
          willingnessId,
          buildingId: clickedBuilding?.buildingId || originalBuildingId || '',
          automationCapability: updated.price_estimation_capability,
          address: {
            street: form.address,
            city: form.area,
            postcode: form.postal,
            coordinates: listingLocation ?? undefined,
          },
          propertyDetails: {
            description: form.description.trim(),
            size: form.size.trim() !== '' ? Number(form.size) : null,
            rooms: form.rooms || '',
            floor: form.floor.trim() !== '' ? Number(form.floor) : null,
            hasElevator: form.hasElevator ?? false,
            hasBalcony: form.hasBalcony ?? false,
            renovationStatus: form.renovationStatus || '',
            buildingType: form.buildingType || '',
          },
          language: i18n.language,
        }).then((result) => {
          if (result === 'started') {
            window.dispatchEvent(new Event('jaakko:listings-changed'));
            return;
          }
          window.dispatchEvent(
            new CustomEvent('jaakko:listing-estimate-not-started', {
              detail: { willingnessId },
            }),
          );
          if (result === 'daily_limit') {
            alert(t('building.offMarket.modal.automaticEstimateDailyLimit'));
          }
        });
      }
      onSaved();
      onClose();
    } catch (error) {
      console.error('Error updating listing:', error);
      alert(t('common.error', 'An error occurred. Please try again.'));
    } finally {
      setIsPublishing(false);
    }
  }, [
    form,
    images,
    willingnessId,
    clickedBuilding,
    originalBuildingId,
    listingLocation,
    onSaved,
    onClose,
    t,
  ]);

  if (!isMounted) return null;

  // Wizard display strings are pure derivations of the form (no commas in the
  // rebuilt address, so displayAddress == form.address, previewCity == area).
  const displayAddress = form ? form.address : '';
  const previewCity = form ? form.area.trim() : '';
  const cityWithPostal = form
    ? [form.postal.trim(), previewCity].filter(Boolean).join(' ')
    : '';
  const addressWithPostal = [displayAddress, cityWithPostal]
    .filter(Boolean)
    .join(', ');

  const cardClass =
    view === 'details'
      ? `amp-flow amp-flow--edit amp-flow--publish${detailsExpanded ? ' amp-flow--details-expanded' : ''}`
      : 'amp-flow amp-flow--edit amp-flow--limited-height';

  // The 3 header stepper dots map to the flow's editable steps; the publish
  // (details) view has none. Clicking a dot jumps straight to that step.
  const resolveStepDot = (): number | null => {
    if (view === 'pickBuilding') return 0;
    if (view === 'size') return 1;
    if (view === 'rooms') return 2;
    return null;
  };
  const goToDot = (index: number) => {
    const stepViews = ['pickBuilding', 'size', 'rooms'] as const;
    setView(stepViews[index] ?? 'pickBuilding');
  };

  return createPortal(
    <div
      className="amp-flow__overlay amp-flow__overlay--edit"
      data-open={isActive ? 'true' : 'false'}
    >
      <div className={cardClass} role="dialog" aria-modal="true">
        <WizardHeader
          hideBack={view === 'details'}
          onBack={() => {
            if (view === 'rooms') setView('size');
            else if (view === 'size') setView('pickBuilding');
            else setView('details');
          }}
          onClose={handleClose}
          activeDot={resolveStepDot()}
          onDotClick={goToDot}
          title={t('building.offMarket.modal.edit.title')}
          titleIcon={<img src={houseIcon} alt="" width={20} height={20} />}
        />
        {view === 'details' && (
          <div className="amp-flow__content amp-flow__content--publish">
            {form ? (
              <ReadyToPublishStep
                form={form}
                displayAddress={displayAddress}
                previewCity={previewCity}
                addressWithPostal={addressWithPostal}
                displaySize={form.size.trim()}
                displayRooms={form.rooms.trim()}
                onBuildingTypeChange={(value) =>
                  updateForm({ buildingType: value })
                }
                onFormChange={updateForm}
                onPublish={handlePublish}
                isPublishing={isPublishing}
                detailsExpanded={detailsExpanded}
                onDetailsExpandedChange={setDetailsExpanded}
                photos={images}
                uploadError={uploadError}
                onUploadPhotos={uploadFiles}
                onRemovePhoto={removeImage}
                onReorderPhotos={reorderImages}
                onOpenPhotoManager={(index) => setLightboxIndex(index)}
                hasSeenPhotoLightbox
                isLoggedIn
                setLoginModalVisible={() => {}}
                ctaLabel={t('building.offMarket.modal.update')}
                showEstimateNotify={false}
                onEditFacts={() => setView('pickBuilding')}
                estimatedPrice={estimate.price}
                estimatedPriceCurrency={estimate.currency}
                estimateStatus={estimate.status}
              />
            ) : (
              <div className="edit-listing-modal__status">
                {loadError
                  ? t('common.error', 'An error occurred. Please try again.')
                  : t('building.offMarket.modal.loading', 'Loading...')}
              </div>
            )}
          </div>
        )}
        {view === 'pickBuilding' && (
          <div className="amp-flow__content amp-flow__content--building">
            <BuildingConfirmStep
              addressWithPostal={addressWithPostal}
              buildingType={form?.buildingType ?? 'apartment_building'}
              onBuildingTypeChange={(value) =>
                updateForm({ buildingType: value })
              }
              onNext={() => setView('size')}
              requiresManualBuildingSelection={false}
              hasSelectedBuilding
            />
            {listingLocation && (
              <FlowMap
                mapCenter={listingLocation}
                mapZoom={PICKER_ZOOM}
                countryOfFeedContent="Finland"
                highlightAddressAt={listingLocation}
                selectBuilding={handleSelectBuilding}
              />
            )}
          </div>
        )}
        {view === 'size' && form && (
          <div className="amp-flow__content amp-flow__content--size">
            <SizeStep
              address={displayAddress}
              area={previewCity}
              size={form.size}
              onSizeChange={(value) => updateForm({ size: value })}
              onNext={() => setView('rooms')}
            />
          </div>
        )}
        {view === 'rooms' && form && (
          <div className="amp-flow__content amp-flow__content--rooms">
            <RoomsStep
              address={displayAddress}
              area={previewCity}
              size={form.size}
              rooms={form.rooms}
              onSelectRooms={(value) => updateForm({ rooms: value })}
              onNext={() => setView('details')}
            />
          </div>
        )}
      </div>
      <ImageLightbox
        isOpen={lightboxIndex !== null && images.length > 0}
        images={images}
        initialIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
        onToggleVersion={toggleImageVersion}
        onRegenerate={regenerateImage}
        isRegenerating={isRegenerating}
        enhancementCredits={enhancementStatus?.credits ?? null}
        onEnhance={regenerateImage}
      />
    </div>,
    document.body,
  );
};

export default EditListingModal;
